import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { COMPANY } from "../lib/content";

const FOOTER_COLS = [
  {
    title: "Institucional",
    links: [
      { label: "Quem somos", to: "/quem-somos" },
      { label: "Missão, Visão e Valores", to: "/quem-somos#mvv" },
      { label: "Diferenciais", to: "/quem-somos#diferenciais" },
      { label: "Metodologia", to: "/metodologia-de-obra" },
    ],
  },
  {
    title: "Serviços",
    links: [
      { label: "Obras Industriais", to: "/servicos/obras-industriais" },
      { label: "Reformas Industriais", to: "/servicos/reformas-industriais" },
      { label: "Obras Comerciais", to: "/servicos/obras-comerciais" },
      { label: "Adequações Estruturais", to: "/servicos/adequacoes-estruturais" },
      { label: "Obras Residenciais", to: "/servicos/obras-residenciais" },
      { label: "Gerenciamento de Obras", to: "/servicos/gerenciamento-de-obras" },
    ],
  },
  {
    title: "Projetos",
    links: [
      { label: "Obras Industriais", to: "/projetos/industriais" },
      { label: "Obras Comerciais", to: "/projetos/comerciais" },
      { label: "Reformas", to: "/projetos/reformas" },
      { label: "Cases Técnicos", to: "/projetos/cases-tecnicos" },
      { label: "Notícias", to: "/noticias" },
      { label: "Contato", to: "/contato" },
    ],
  },
];

const LEGAL = [
  { label: "Política de Privacidade", to: "/politica-de-privacidade" },
  { label: "Termos de Uso", to: "/termos-de-uso" },
  { label: "LGPD", to: "/lgpd" },
  { label: "Política de Cookies", to: "/politica-de-cookies" },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-sca-black text-white pt-24 md:pt-32 pb-10 overflow-hidden"
    >
      <div className="absolute inset-0 sca-grid-lines opacity-40 pointer-events-none" aria-hidden />
      <div className="absolute inset-0 sca-noise opacity-60 pointer-events-none" aria-hidden />

      <div className="sca-container relative">
        {/* Massive CTA */}
        <div className="border-t border-sca-lineDark pt-12 md:pt-16 mb-20 md:mb-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-8">
              <p className="sca-overline text-white/60 mb-6">[ Contato ]</p>
              <h2 className="font-sans text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tightest">
                Sua obra precisa de <span className="sca-serif text-sca-red">previsibilidade</span>.
                <br />Vamos estruturá-la.
              </h2>
            </div>
            <div className="md:col-span-4 flex md:items-end md:justify-end">
              <Link
                to="/contato"
                data-testid="footer-cta-contact"
                className="btn-sca-primary"
              >
                Solicitar avaliação <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Map embed — antes dos dados institucionais */}
        <div className="border border-sca-lineDark bg-sca-graphite mb-16 md:mb-20">
          <iframe
            title="Localização — SCA Construtora"
            src="https://www.google.com/maps?q=Rua+Volunt%C3%A1rios+de+Piracicaba%2C+367+Piracicaba+SP&hl=pt-BR&z=15&output=embed"
            className="w-full h-[280px] md:h-[360px] grayscale opacity-90"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 0 }}
            allowFullScreen
          />
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-4">
            <img
              src={COMPANY.logos.negative}
              alt="SCA Construtora"
              className="h-12 md:h-14 w-auto mb-6"
              draggable={false}
            />
            <p className="text-sm md:text-base text-white/65 leading-relaxed max-w-sm">
              Engenharia organizada para execução de obras industriais, comerciais e estruturais de
              alta complexidade. Método, controle e responsabilidade técnica em cada etapa.
            </p>

            {/* Social */}
            <div className="mt-8 flex items-center gap-3">
              {[
                { Icon: Instagram, href: COMPANY.socials.instagram, label: "Instagram" },
                { Icon: Facebook, href: COMPANY.socials.facebook, label: "Facebook" },
                { Icon: Linkedin, href: COMPANY.socials.linkedin, label: "LinkedIn" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="inline-flex w-11 h-11 items-center justify-center border border-white/15 text-white/80 hover:bg-sca-red hover:border-sca-red hover:text-white transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {FOOTER_COLS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <p className="sca-overline text-white/55 mb-5">{col.title}</p>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.to}`}
                      className="text-[14px] text-white/75 hover:text-sca-red transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact column */}
          <div className="col-span-2 md:col-span-2">
            <p className="sca-overline text-white/55 mb-5">Contato</p>
            <ul className="space-y-4 text-[14px] text-white/80">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-sca-red flex-shrink-0" />
                <span>
                  {COMPANY.address.line1}<br />
                  {COMPANY.address.line2}<br />
                  {COMPANY.address.city} — {COMPANY.address.zip}
                </span>
              </li>
              <li>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="flex gap-3 hover:text-sca-red transition-colors"
                  data-testid="footer-phone-link"
                >
                  <Phone className="w-4 h-4 mt-0.5 text-sca-red" />
                  {COMPANY.phone}
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${COMPANY.whatsappRaw}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex gap-3 hover:text-sca-red transition-colors"
                  data-testid="footer-whatsapp-link"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 mt-0.5 text-sca-red" fill="currentColor" aria-hidden>
                    <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.92c0 2.1.55 4.15 1.6 5.96L2 22l4.25-1.65a9.9 9.9 0 0 0 5.78 1.84h.01c5.46 0 9.91-4.45 9.91-9.92 0-2.65-1.03-5.14-2.9-7.36ZM12.04 20.3a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-2.52.98.86-2.45-.2-.32a8.27 8.27 0 1 1 6.35 3.12Z"/>
                  </svg>
                  {COMPANY.whatsapp}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex gap-3 hover:text-sca-red transition-colors break-all"
                  data-testid="footer-email-link"
                >
                  <Mail className="w-4 h-4 mt-0.5 text-sca-red" />
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex gap-3 text-white/65">
                <Clock className="w-4 h-4 mt-0.5 text-sca-red" />
                {COMPANY.hours}
              </li>
            </ul>
          </div>
        </div>

        {/* Legal bar */}
        <div className="mt-12 pt-8 border-t border-sca-lineDark flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="text-[11px] tracking-[0.16em] uppercase text-white/50">
            © {new Date().getFullYear()} SCA Construtora — Todos os direitos reservados. CNPJ {COMPANY.cnpj}
          </p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {LEGAL.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-legal-${l.to}`}
                  className="text-[11px] tracking-[0.16em] uppercase text-white/60 hover:text-sca-red transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
